import { useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import {
  importHistoricalData,
  importVendorStats,
  getHistoricalImportTemplateUrl,
  type HistoricalImportFormat,
  type HistoricalImportResult,
} from '../../api/importApi';

const FORMAT_INFO: Record<HistoricalImportFormat, { label: string; description: string; hasTemplate: boolean }> = {
  Summary: {
    label: 'Summary format',
    description: 'Columns: survey_date, engagement_index, q1_score…qN_score. Powers trend charts and benchmarks only. Team breakdown and comments will show "Not available for imported data".',
    hasTemplate: true,
  },
  Full: {
    label: 'Full format',
    description: 'Columns: survey_date, respondent_id (anonymised), team, q1_score…qN_score, q1_comment…qN_comment. Treated identically to a natively-run survey.',
    hasTemplate: true,
  },
  Vendor: {
    label: 'Vendor export (Leapsome / Culture Amp)',
    description: 'Upload the CSV exported directly from your previous survey tool. Tab-separated, one row per question per respondent. Respondents are detected automatically — no respondent ID needed.',
    hasTemplate: false,
  },
};

export function HistoricalImport() {
  const { instance, accounts } = useMsal();
  const [format, setFormat] = useState<HistoricalImportFormat>('Summary');

  const [status, setStatus] = useState<'idle' | 'importing' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<HistoricalImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const [statsStatus, setStatsStatus] = useState<'idle' | 'importing' | 'done' | 'error'>('idle');
  const [statsResult, setStatsResult] = useState<HistoricalImportResult | null>(null);
  const [statsErrorMsg, setStatsErrorMsg] = useState('');
  const statsFileRef = useRef<HTMLInputElement>(null);

  const getToken = async () => {
    const r = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return r.accessToken;
  };

  const handleStatsImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatsStatus('importing');
    setStatsErrorMsg('');
    setStatsResult(null);

    try {
      const token = await getToken();
      const res = await importVendorStats(token, file);
      setStatsResult(res);
      setStatsStatus('done');
    } catch (err) {
      setStatsStatus('error');
      setStatsErrorMsg(err instanceof Error ? err.message : 'Stats import failed.');
    } finally {
      if (statsFileRef.current) statsFileRef.current.value = '';
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatus('importing');
    setErrorMsg('');
    setResult(null);

    try {
      const token = await getToken();
      const res = await importHistoricalData(token, file, format);
      setResult(res);
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Import failed. Please check the file and try again.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>Historical Data Import</h2>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Upload past survey exports to populate trend history before go-live.
        Imported surveys are labelled "Imported" throughout GridPulse.
      </p>

      {/* Format selector */}
      <section style={{ marginBottom: '1.5rem' }}>
        <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '0.75rem' }}>Import format</strong>
        {(Object.keys(FORMAT_INFO) as HistoricalImportFormat[]).map(f => (
          <label
            key={f}
            style={{
              display: 'flex', gap: '0.75rem', padding: '0.75rem 1rem',
              border: `2px solid ${format === f ? 'var(--color-primary)' : 'var(--color-border)'}`,
              borderRadius: 8, marginBottom: '0.5rem', cursor: 'pointer',
              background: format === f ? 'var(--color-primary-light)' : 'var(--color-surface)',
            }}
          >
            <input type="radio" name="format" value={f} checked={format === f} onChange={() => setFormat(f)}
              style={{ marginTop: 2 }} />
            <div>
              <strong style={{ fontSize: '0.9rem' }}>{FORMAT_INFO[f].label}</strong>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                {FORMAT_INFO[f].description}
              </p>
            </div>
          </label>
        ))}
      </section>

      {/* Template downloads — hidden for Vendor (upload vendor export directly) */}
      {FORMAT_INFO[format].hasTemplate && (
        <section style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <a
            href={getHistoricalImportTemplateUrl('Summary')}
            download
            className="btn btn-ghost"
            style={{ minHeight: 38, padding: '0 1rem', fontSize: '0.875rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            ↓ Summary template
          </a>
          <a
            href={getHistoricalImportTemplateUrl('Full')}
            download
            className="btn btn-ghost"
            style={{ minHeight: 38, padding: '0 1rem', fontSize: '0.875rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            ↓ Full template
          </a>
        </section>
      )}

      {/* Upload */}
      <section style={{ marginBottom: '1.5rem' }}>
        <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
          Upload {FORMAT_INFO[format].label} CSV
        </strong>
        <label
          className="btn btn-primary"
          style={{ minHeight: 44, padding: '0 1.25rem', cursor: 'pointer', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {status === 'importing' ? 'Importing…' : 'Choose file and import'}
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            disabled={status === 'importing'}
            onChange={handleImport}
          />
        </label>
      </section>

      {/* Result */}
      {status === 'done' && result && (
        <div style={{ background: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)', borderRadius: 8, padding: '1rem' }}>
          <strong style={{ color: 'var(--color-success)' }}>Import complete</strong>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
            {result.surveysCreated} survey{result.surveysCreated !== 1 ? 's' : ''} created
            {result.responsesImported > 0 && `, ${result.responsesImported} response${result.responsesImported !== 1 ? 's' : ''} imported`}.
          </p>
          {result.warnings?.length > 0 && (
            <details style={{ marginTop: '0.75rem' }}>
              <summary style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
                {result.warnings.length} warning{result.warnings.length !== 1 ? 's' : ''}
              </summary>
              <ul style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {status === 'error' && (
        <p role="alert" style={{ color: 'var(--color-error)', fontSize: '0.9rem' }}>{errorMsg}</p>
      )}

      {/* Benchmark stats upload — Vendor format only */}
      {format === 'Vendor' && (
        <section style={{
          marginTop: '2rem', paddingTop: '1.5rem',
          borderTop: '1px solid var(--color-border)',
        }}>
          <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
            Upload benchmark stats <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(optional)</span>
          </strong>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem', marginBottom: '0.75rem', lineHeight: 1.5 }}>
            Upload <code>survey_stats.csv</code> to store industry and overall benchmarks alongside each question average.
            Upload the responses file first, then upload the stats file for the same survey date.
          </p>
          <label
            className="btn btn-ghost"
            style={{ minHeight: 40, padding: '0 1rem', cursor: 'pointer', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {statsStatus === 'importing' ? 'Importing…' : '↑ Choose stats file'}
            <input
              ref={statsFileRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              disabled={statsStatus === 'importing'}
              onChange={handleStatsImport}
            />
          </label>

          {statsStatus === 'done' && statsResult && (
            <div style={{ marginTop: '0.75rem', background: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)', borderRadius: 8, padding: '0.75rem 1rem' }}>
              <strong style={{ color: 'var(--color-success)', fontSize: '0.9rem' }}>Benchmarks stored</strong>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                {statsResult.responsesImported} question benchmark{statsResult.responsesImported !== 1 ? 's' : ''} saved.
              </p>
              {statsResult.warnings?.length > 0 && (
                <details style={{ marginTop: '0.5rem' }}>
                  <summary style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
                    {statsResult.warnings.length} warning{statsResult.warnings.length !== 1 ? 's' : ''}
                  </summary>
                  <ul style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                    {statsResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          {statsStatus === 'error' && (
            <p role="alert" style={{ marginTop: '0.5rem', color: 'var(--color-error)', fontSize: '0.85rem' }}>{statsErrorMsg}</p>
          )}
        </section>
      )}
    </div>
  );
}
